import {FeedbackFormCompletionResource} from '../../shared/types/generated/feedbackFormCompletionResource';
import {FeedbackCompletionMode} from '../../shared/types/generated/feedbackCompletionMode';
import {FeedbackCompletionResultDisplay} from '../../shared/types/generated/feedbackCompletionResultDisplay';
import {FeedbackPassingThresholdType} from '../../shared/types/generated/feedbackPassingThresholdType';
import {FeedbackQuestionType} from '../../shared/types/generated/feedbackQuestionType';
import {
  FeedbackStructureQuestionFormQuestion,
  FeedbackStructureRatingQuestion,
  FeedbackStructureSliderQuestion,
} from './feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';

export interface CompletionThresholdBounds {
  min: number;
  max: number;
}

export function normalizeCompletionMode(completion: FeedbackFormCompletionResource | null | undefined): FeedbackCompletionMode {
  const value = completion?.mode;
  return Object.values(FeedbackCompletionMode).includes(value as FeedbackCompletionMode)
    ? value as FeedbackCompletionMode
    : FeedbackCompletionMode.DISABLED;
}

export function normalizeCompletionResultDisplay(
  completion: FeedbackFormCompletionResource | null | undefined
): FeedbackCompletionResultDisplay {
  const value = completion?.result_display;
  return Object.values(FeedbackCompletionResultDisplay).includes(value as FeedbackCompletionResultDisplay)
    ? value as FeedbackCompletionResultDisplay
    : FeedbackCompletionResultDisplay.PERCENTAGE;
}

export function normalizeCompletionThresholdType(
  completion: FeedbackFormCompletionResource | null | undefined
): FeedbackPassingThresholdType {
  const value = completion?.passing_threshold?.type;
  return Object.values(FeedbackPassingThresholdType).includes(value as FeedbackPassingThresholdType)
    ? value as FeedbackPassingThresholdType
    : FeedbackPassingThresholdType.PERCENTAGE;
}

export function normalizeCompletionText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue && trimmedValue !== '<p><br/></p>' && trimmedValue !== '<p><br></p>'
    ? value
    : null;
}

export function getCompletionThresholdBounds(
  questions: FeedbackStructureQuestionFormQuestion[]
): CompletionThresholdBounds {
  return questions.reduce<CompletionThresholdBounds>((bounds, question) => {
    const questionBounds = getQuestionScoreBounds(question);
    return {
      min: bounds.min + questionBounds.min,
      max: bounds.max + questionBounds.max,
    };
  }, {min: 0, max: 0});
}

export function clampCompletionThresholdValue(
  value: number,
  bounds: CompletionThresholdBounds
): number {
  return Math.min(Math.max(value, bounds.min), bounds.max);
}

function getQuestionScoreBounds(question: FeedbackStructureQuestionFormQuestion): CompletionThresholdBounds {
  switch (question.type) {
    case FeedbackQuestionType.SINGLE_SELECTION:
    case FeedbackQuestionType.DROPDOWN:
      return getSingleChoiceScoreBounds(question);
    case FeedbackQuestionType.MULTI_SELECTION:
    case FeedbackQuestionType.ORDERING:
      return getAggregateOptionScoreBounds(question);
    case FeedbackQuestionType.SLIDER:
      return getSliderScoreBounds(question);
    case FeedbackQuestionType.RATING:
      return getRatingScoreBounds(question);
    case FeedbackQuestionType.TEXT_INPUT:
    case FeedbackQuestionType.DATE_TIME:
    case FeedbackQuestionType.TIME:
    case FeedbackQuestionType.DATE:
    case FeedbackQuestionType.FILES:
      return getFixedQuestionScoreBounds(question.points);
  }
}

function getFixedQuestionScoreBounds(points: number): CompletionThresholdBounds {
  return {
    min: Math.min(points, 0),
    max: Math.max(points, 0),
  };
}

function getSingleChoiceScoreBounds(question: FeedbackStructureQuestionFormQuestion): CompletionThresholdBounds {
  if (shouldUseWholeQuestionPoints(question)) {
    return getFixedQuestionScoreBounds(question.points);
  }

  const optionPoints = question.options.map((option) => option.points ?? 0);
  return {
    min: Math.min(0, ...optionPoints),
    max: Math.max(0, ...optionPoints),
  };
}

function getAggregateOptionScoreBounds(question: FeedbackStructureQuestionFormQuestion): CompletionThresholdBounds {
  if (shouldUseWholeQuestionPoints(question)) {
    return getFixedQuestionScoreBounds(question.points);
  }

  return question.options.reduce<CompletionThresholdBounds>((bounds, option) => {
    const points = option.points ?? 0;
    return {
      min: bounds.min + Math.min(points, 0),
      max: bounds.max + Math.max(points, 0),
    };
  }, {min: 0, max: 0});
}

function shouldUseWholeQuestionPoints(question: FeedbackStructureQuestionFormQuestion): boolean {
  return question.options.every((option) => (option.points ?? 0) === 0);
}

function getSliderScoreBounds(question: FeedbackStructureSliderQuestion): CompletionThresholdBounds {
  if (!question.configuration.use_value_as_points) {
    return getFixedQuestionScoreBounds(question.points);
  }

  return {
    min: Math.min(question.configuration.min_value, question.configuration.max_value, 0),
    max: Math.max(question.configuration.min_value, question.configuration.max_value, 0),
  };
}

function getRatingScoreBounds(question: FeedbackStructureRatingQuestion): CompletionThresholdBounds {
  if (!question.configuration.use_rating_as_points) {
    return getFixedQuestionScoreBounds(question.points);
  }

  return {
    min: 0,
    max: 5,
  };
}
